<template>
  <div class="deliveries-table">
    <div class="deliveries-table__toolbar">
      <v-select
        v-model="statusFilter"
        :items="statusOptions"
        item-text="label"
        item-value="value"
        outlined
        dense
        hide-details
        clearable
        label="Status"
        class="deliveries-table__filter" />
    </div>

    <v-data-table
      class="elevation-0 rounded-0 custom-table"
      :footer-props="{
        showFirstLastPage: true,
        firstIcon: 'mdi-chevron-double-left',
        lastIcon: 'mdi-chevron-double-right',
        prevIcon: 'mdi-chevron-left',
        nextIcon: 'mdi-chevron-right',
        itemsPerPageText: 'Row/Page',
        itemsPerPageOptions: [15, 30, 50, 100, 200, 300, 400, 500, -1],
      }"
      :headers="headers"
      :items="deliveries"
      :options.sync="options"
      :loading="loading"
      :server-items-length="total">
      <template v-slot:[`item.email`]="{ item }">
        <span class="deliveries-table__email">{{ recipientOf(item) }}</span>
      </template>

      <template v-slot:[`item.companyName`]="{ item }">
        {{ companyOf(item) }}
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <v-chip x-small :color="statusMetaOf(item).color" text-color="white">
          {{ statusMetaOf(item).label }}
        </v-chip>
      </template>

      <template v-slot:[`item.sentAt`]="{ item }">
        <span v-if="sentAtOf(item)">{{ $formatDate(sentAtOf(item), 'DD/MM/YYYY HH:mm') }}</span>
        <span v-else class="deliveries-table__muted">—</span>
      </template>

      <template v-slot:[`item.reason`]="{ item }">
        <span v-if="reasonOf(item)" class="deliveries-table__reason">{{ reasonOf(item) }}</span>
        <span v-else class="deliveries-table__muted">—</span>
      </template>

      <template v-slot:no-data>
        <div class="text-center d-flex flex-column">
          <img width="200" src="/img/notFound.svg" class="py-4 my-4" style="opacity: 0.54" />
          <p class="font-weight-medium">No deliveries to show.</p>
        </div>
      </template>

      <template v-slot:loading>
        <div class="text-center d-flex flex-column">
          <img width="200" src="/img/loading.svg" class="py-4 my-4" style="opacity: 0.54" />
          <p class="font-weight-medium">Loading deliveries...</p>
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
  import { $formatDate } from '@Utils';
  import { warn } from '@/utils/debugLogger';
  import headers from '@ReleaseNotes/config/deliveries.headers';
  import { ReleaseDeliveryStatusMeta } from '@ReleaseNotes/config/enums';

  const UNKNOWN_STATUS = { label: 'Unknown', color: 'grey' };

  /**
   * DeliveriesTable — paged delivery report for ONE publication.
   *
   * Self-fetching, and deliberately keeps its rows in LOCAL state rather than reading
   * `RELEASE_NOTES/GET_DELIVERIES`: PublicationHistory can have several publications expanded at
   * once, and the shared Vuex slice holds only the last response — every table would then show the
   * same rows. `FETCH_DELIVERIES` resolves with the response body, so each instance uses its own.
   *
   * Response shape is unverified (addendum §A.5), hence the local `unwrapList` helper accepting
   * both a bare array and `{ items, total }`.
   */
  export default {
    name: 'DeliveriesTable',

    props: {
      publicationId: { type: [String, Number], default: null },
    },

    data() {
      return {
        headers,
        deliveries: [],
        total: 0,
        loading: false,
        statusFilter: null,
        options: { page: 1, itemsPerPage: 15, sortBy: [], sortDesc: [] },
        // Monotonic request id — only the newest response writes state.
        requestSeq: 0,
      };
    },

    computed: {
      statusOptions() {
        return Object.keys(ReleaseDeliveryStatusMeta).map((key) => ({
          value: Number(key),
          label: ReleaseDeliveryStatusMeta[key].label,
        }));
      },
    },

    watch: {
      options: {
        deep: true,
        handler() {
          this.fetchDeliveries();
        },
      },

      statusFilter() {
        // A filter change invalidates the current page window.
        if (this.options.page !== 1) {
          this.options = { ...this.options, page: 1 };
          return;
        }
        this.fetchDeliveries();
      },

      publicationId() {
        this.deliveries = [];
        this.total = 0;
        if (this.options.page !== 1) {
          this.options = { ...this.options, page: 1 };
          return;
        }
        this.fetchDeliveries();
      },
    },

    created() {
      this.fetchDeliveries();
    },

    beforeDestroy() {
      // Discard any response still in flight so it can't write into a destroyed component.
      this.requestSeq += 1;
    },

    methods: {
      $formatDate,

      /**
       * Normalises an unverified list response.
       * @param {*} data raw response body
       * @returns {{ items: Array, total: number }}
       */
      unwrapList(data) {
        if (Array.isArray(data)) return { items: data, total: data.length };
        if (data && typeof data === 'object') {
          const items = Array.isArray(data.items) ? data.items : [];
          return {
            items,
            total: Number.isFinite(Number(data.total)) ? Number(data.total) : items.length,
          };
        }
        return { items: [], total: 0 };
      },

      statusMetaOf(item) {
        const status = item && item.status;
        return ReleaseDeliveryStatusMeta[status] || UNKNOWN_STATUS;
      },

      recipientOf(item) {
        const row = item || {};
        return row.email || row.recipientEmail || row.recipient || '(no address)';
      },

      companyOf(item) {
        const row = item || {};
        const company = row.company || {};
        return row.companyName || company.name || '—';
      },

      sentAtOf(item) {
        const row = item || {};
        return row.sentAt || row.sentAtUtc || row.deliveredAt || null;
      },

      /**
       * Failure / suppression explanation. A string is trusted; a bare numeric suppression code
       * renders as `Reason {code}` because the `ReleaseSuppressionReason` members are undefined on
       * the client (addendum §A.4) and inventing labels would be wrong.
       */
      reasonOf(item) {
        const row = item || {};
        const candidates = [
          row.error,
          row.errorMessage,
          row.reason,
          row.suppressionReasonText,
          row.suppressionReason,
        ];
        const text = candidates.find(
          (value) => typeof value === 'string' && value.trim().length > 0,
        );
        if (text) return text.trim();

        const code = candidates.find((value) => typeof value === 'number');
        return code === undefined ? '' : `Reason ${code}`;
      },

      async fetchDeliveries() {
        if (this.publicationId == null) return;

        this.requestSeq += 1;
        const seq = this.requestSeq;
        this.loading = true;

        try {
          const data = await this.$store.dispatch('RELEASE_NOTES/FETCH_DELIVERIES', {
            publicationId: this.publicationId,
            params: {
              page: this.options.page,
              itemsPerPage: this.options.itemsPerPage,
              status: this.statusFilter,
            },
          });
          if (seq !== this.requestSeq) return;
          const { items, total } = this.unwrapList(data);
          this.deliveries = items;
          this.total = total;
        } catch (err) {
          if (seq !== this.requestSeq) return;
          warn('[DeliveriesTable] failed to load deliveries', err);
        } finally {
          if (seq === this.requestSeq) this.loading = false;
        }
      },
    },
  };
</script>

<style scoped lang="scss">
  .deliveries-table__toolbar {
    display: flex;
    justify-content: flex-end;
    padding: 8px 0;
  }

  .deliveries-table__filter {
    max-width: 220px;
  }

  .deliveries-table__email {
    font-size: 0.8125rem;
  }

  .deliveries-table__reason {
    font-size: 0.75rem;
    opacity: 0.8;
  }

  .deliveries-table__muted {
    opacity: 0.5;
  }
</style>
